// The changelog, read inside the app.
//
// Reads versionLog, which is admin-read and write-denied, so the gate is a
// Firestore rule rather than whether anyone thinks to type a URL. It was a
// file in the bundle first; that hid it from the members' interface and from
// nobody else.
//
// CHANGELOG.md in the repo is still the source of truth — the deploy copies
// each release's section into its versionLog row (scripts/publish-release.js).
// Nothing is authored here or in the console, so the two cannot drift.
//
// A release only appears once it has been deployed, which is the point: this
// answers "what is live", not "what is written down".
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, CircularProgress, Chip,
} from '@mui/material';

// Inline spans: `code` and **bold**, which is all the file uses.
function renderInline(text, keyBase) {
  const out = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('`')) {
      out.push(
        <Box key={`${keyBase}-c${i++}`} component="code"
          sx={{ fontFamily: 'monospace', fontSize: '0.82em', px: 0.6, py: 0.1,
                borderRadius: '3px', backgroundColor: 'rgba(201,168,76,0.12)', color: '#C9A84C' }}>
          {tok.slice(1, -1)}
        </Box>
      );
    } else {
      out.push(<strong key={`${keyBase}-b${i++}`}>{tok.slice(2, -2)}</strong>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * Parse the release sections only.
 *
 * Everything above the first release heading is instructions for writing an
 * entry — useful in the file, noise on screen. A release heading is a level-2
 * heading starting with a digit, which is what "## 1.0.0 — 2026-08-25" is and
 * "## How this works" is not.
 */
function parseReleases(md) {
  const lines = md.split('\n');
  const releases = [];
  let cur = null;
  let group = null;
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h2 = /^##\s+(\d.*)$/.exec(line);
    if (h2) {
      const [version, ...rest] = h2[1].split('—');
      cur = { version: version.trim(), date: rest.join('—').trim(), groups: [], intro: [] };
      releases.push(cur);
      group = null;
      continue;
    }
    if (!cur) continue;                       // still in the preamble
    const h3 = /^###\s+(.*)$/.exec(line);
    if (h3) { group = { title: h3[1].trim(), items: [] }; cur.groups.push(group); continue; }
    const bullet = /^-\s+(.*)$/.exec(line);
    if (bullet && group) { group.items.push(bullet[1]); continue; }
    // A wrapped bullet continues the previous one.
    if (group && group.items.length && /^\s{2,}\S/.test(raw)) {
      group.items[group.items.length - 1] += ' ' + line.trim();
      continue;
    }
    if (!group && line.trim()) cur.intro.push(line.trim());
  }
  return releases;
}

const GROUP_COLOR = {
  Added: '#27AE60',
  Changed: '#C9A84C',
  Fixed: '#3498DB',
  Data: '#8E44AD',
};

/**
 * @param markdown  One release's section, when the caller already has it —
 *                  the Versions tab reads versionLog itself, so making it
 *                  refetch to open a row would be work for nothing. Omit to
 *                  load every release.
 */
export default function ChangelogDialog({ open, onClose, markdown = '' }) {
  const [state, setState] = useState({ loading: true, releases: [], error: '' });

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setState({ loading: true, releases: [], error: '' });
    if (markdown) { setState({ loading: false, releases: parseReleases(markdown), error: '' }); return undefined; }
    getDocs(query(collection(db, 'versionLog'), orderBy('releasedAt', 'desc')))
      .then(snap => {
        // Each row holds its own section verbatim, so joining them back into
        // one document lets the same parser read one release or all of them.
        const md = snap.docs.map(d => d.data().changelog || '').filter(Boolean).join('\n\n');
        if (alive) setState({ loading: false, releases: parseReleases(md), error: '' });
      })
      .catch(e => { if (alive) setState({ loading: false, releases: [], error: e.message }); });
    return () => { alive = false; };
  }, [open, markdown]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
        {state.releases.length === 1 && markdown
          ? `What changed in ${state.releases[0].version}`
          : "What's changed"}
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#0D1E35', color: 'white' }}>
        {state.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} sx={{ color: '#C9A84C' }} />
          </Box>
        ) : state.error ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', py: 2 }}>
            Couldn&apos;t load the changelog ({state.error}).
          </Typography>
        ) : state.releases.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', py: 2 }}>
            No releases recorded yet — one lands here on the next deploy.
          </Typography>
        ) : state.releases.map(rel => (
          <Box key={rel.version} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
              <Typography sx={{ color: '#C9A84C', fontWeight: 700, fontSize: '1.05rem' }}>
                {rel.version}
              </Typography>
              {rel.date && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>
                  {rel.date}
                </Typography>
              )}
            </Box>
            {rel.intro.map((p, i) => (
              <Typography key={i} sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', mb: 1.5 }}>
                {renderInline(p, `i${i}`)}
              </Typography>
            ))}
            {rel.groups.map(g => (
              <Box key={g.title} sx={{ mb: 1.5 }}>
                <Chip
                  label={g.title} size="small"
                  sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.5px',
                        textTransform: 'uppercase', mb: 0.75,
                        backgroundColor: `${GROUP_COLOR[g.title] || '#7F8C8D'}22`,
                        color: GROUP_COLOR[g.title] || '#BDC3C7' }}
                />
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {g.items.map((it, i) => (
                    <Box component="li" key={i}
                      sx={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.82rem', mb: 0.5, lineHeight: 1.5 }}>
                      {renderInline(it, `${g.title}${i}`)}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: '#0D1E35' }}>
        <Button onClick={onClose} sx={{ color: '#C9A84C' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
